import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const LEGAL_PAGES = {
  'datenschutz': {
    titleKey: 'page_privacy_title',
    contentKey: 'page_privacy_content',
    defaultTitle: { de: 'Datenschutzerklärung', en: 'Privacy Policy' },
    defaultContent: { 
      de: 'Hier steht Ihre Datenschutzerklärung.',
      en: 'Your privacy policy goes here.'
    }
  },
  'agb': {
    titleKey: 'page_terms_title',
    contentKey: 'page_terms_content',
    defaultTitle: { de: 'Allgemeine Geschäftsbedingungen', en: 'Terms and Conditions' },
    defaultContent: { 
      de: 'Hier stehen Ihre Allgemeinen Geschäftsbedingungen.',
      en: 'Your terms and conditions go here.'
    }
  },
  'impressum': {
    titleKey: 'page_imprint_title',
    contentKey: 'page_imprint_content',
    defaultTitle: { de: 'Impressum', en: 'Imprint' },
    defaultContent: { 
      de: 'Hier steht Ihr Impressum.',
      en: 'Your imprint goes here.'
    }
  },
  'vorbehalten': {
    titleKey: 'page_reserved_title',
    contentKey: 'page_reserved_content',
    defaultTitle: { de: 'Vorbehalten', en: 'Reserved' },
    defaultContent: { 
      de: 'Diese Seite ist vorbehalten.',
      en: 'This page is reserved.'
    }
  }
};

export default function LegalPage() {
  const { page } = useParams();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { settings, loading } = useSiteSettings();

  // Determine page from URL path or param
  const pageName = page || location.pathname.replace('/', '');

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageName]);

  const pageConfig = LEGAL_PAGES[pageName];
  
  if (!pageConfig) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24 text-center">
          <h1 className="font-heading text-4xl mb-4">{t('page_not_found')}</h1>
          <Link to="/" className="btn-ghost">{t('back_to_home')}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const getTitle = () => {
    if (!settings) return pageConfig.defaultTitle[language] || pageConfig.defaultTitle.de;
    const key = language === 'en' ? `${pageConfig.titleKey}_en` : pageConfig.titleKey;
    return settings[key] || pageConfig.defaultTitle[language] || pageConfig.defaultTitle.de;
  };

  const getContent = () => {
    if (!settings) return pageConfig.defaultContent[language] || pageConfig.defaultContent.de;
    const key = language === 'en' ? `${pageConfig.contentKey}_en` : pageConfig.contentKey;
    return settings[key] || pageConfig.defaultContent[language] || pageConfig.defaultContent.de;
  };

  const title = getTitle();
  const content = getContent();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 flex justify-center">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* SEO */}
      <Helmet>
        <title>{title} | {settings?.site_name || 'Tim Automobile'}</title>
        <meta name="description" content={`${title} - ${settings?.site_name || 'Tim Automobile'}`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-12">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} />
            <span className="text-sm uppercase tracking-wider">{t('back_to_home')}</span>
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-light" data-testid="legal-title">
            {title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24" data-testid="legal-content">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <div className="bg-card border border-border p-8 md:p-12">
            <div 
              className="prose prose-lg max-w-none text-muted-foreground leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground prose-headings:font-light
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:mb-4
                prose-ul:my-4 prose-ul:pl-6
                prose-li:mb-2
                prose-strong:text-foreground prose-strong:font-medium
                prose-a:text-gold prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }}
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
